/**
 * ReaderCenterShell — Shared center panel wrapper for reader pages
 *
 * Renders: flex-1 flex flex-col min-w-0 wrapper, h-12 header bar,
 * scrollable body, optional footer. Default article-shaped loading skeleton.
 *
 * Loading overlay uses a 150ms delay + CSS opacity fade so fast fetches
 * never flash a spinner.
 */

import { type ReactNode, useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';

interface ReaderCenterShellProps {
  headerLeft?: ReactNode;
  headerContent?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  isLoading?: boolean;
  loadingSkeleton?: ReactNode;
  className?: string;
}

const LOADING_DELAY_MS = 150;

export function ReaderCenterShell({
  headerLeft,
  headerContent,
  children,
  footer,
  isLoading = false,
  loadingSkeleton,
  className = '',
}: ReaderCenterShellProps) {
  // Delayed visibility: only show the overlay after 150ms of continuous loading
  const [showOverlay, setShowOverlay] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isLoading) {
      timerRef.current = setTimeout(() => setShowOverlay(true), LOADING_DELAY_MS);
    } else {
      if (timerRef.current) clearTimeout(timerRef.current);
      setShowOverlay(false);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isLoading]);

  return (
    <div className={`flex-1 flex flex-col min-w-0 ${className}`}>
      {/* Header bar — h-12 aligns with sidebars */}
      <div className="h-12 flex items-center justify-between px-4 border-b border-line/60 bg-white/80 shrink-0">
        <div className="flex items-center gap-2">
          {headerLeft}
        </div>
        <div className="flex items-center gap-2">
          {headerContent}
        </div>
      </div>

      {/* Body — overlay loading keeps previous content visible */}
      <div className="relative flex-1 flex flex-col min-h-0">
        {children}
        <div
          className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none transition-opacity duration-200"
          style={{
            opacity: showOverlay ? 1 : 0,
            backgroundColor: showOverlay ? 'rgba(255,255,255,0.6)' : 'transparent',
            pointerEvents: showOverlay ? 'auto' : 'none',
          }}
        >
          {showOverlay && <Loader2 size={20} className="animate-spin text-subtle" />}
        </div>
      </div>

      {/* Optional footer */}
      {footer}
    </div>
  );
}

export default ReaderCenterShell;
